import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Icon from './Icon';
import Spinner from './Spinner';
import { auth, firebase, db } from '../services/firebase';

// Ensure TS knows about gtag in this file scope to prevent strict compiler errors
declare global {
    interface Window {
        gtag: (...args: any[]) => void;
    }
}

interface PricingModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialView?: 'overview' | 'credits' | 'subscription';
}

type ViewMode = 'overview' | 'credits' | 'subscription' | 'verify_phone';
type TransactionFilter = 'all' | 'gained' | 'consumed';

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose, initialView = 'overview' }) => {
    const { t } = useLanguage();
    const { user, buyCredits, transactions, checkPaymentStatus, pendingTx, clearPendingOrder } = useAuth();
    
    const [viewMode, setViewMode] = useState<ViewMode>(initialView);
    const [transactionFilter, setTransactionFilter] = useState<TransactionFilter>('consumed');
    
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [verifying, setVerifying] = useState(false);

    // --- OTP State ---
    const [pendingTransaction, setPendingTransaction] = useState<{id: string, type: 'subscription' | 'addon'} | null>(null);
    const [mobileInput, setMobileInput] = useState('');
    const [otp, setOtp] = useState('');
    const [confirmationResult, setConfirmationResult] = useState<any>(null);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [otpStep, setOtpStep] = useState<'input' | 'verify'>('input');
    const recaptchaVerifier = useRef<any>(null);

    // --- PRICING DATA CONFIGURATION ---
    const subscriptions = [
        {
            id: 'basic',
            name: 'Basic',
            priceDisplay: 'Free',
            price: 0,
            originalPriceDisplay: '',
            originalPrice: 0,
            discountLabel: '',
            swarnas: 0,
            subDesc: "Starter Access",
            features: [
                "150 Joining Bonus Included",
                "10 Generations / Month Cap",
                "Standard Drafts Only",
                "Community Support"
            ],
            capacities: ["~10 Matchmaker Sets","~10 Nameplate Studio","~10 Sketch to Real"],
            containerClass: 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10',
            buttonClass: 'bg-slate-100 text-slate-400 dark:bg-white/10 dark:text-slate-500 cursor-default',
            badge: null
        },
        {
            id: 'bronze', 
            name: 'Bronze', 
            priceDisplay: '₹99', 
            price: 99,
            originalPriceDisplay: '₹199',
            originalPrice: 199,
            discountLabel: '50% OFF',
            swarnas: 190, 
            subDesc: "190 Swarnas / 30 Days",
            features: ["+ 190 Swarnas (Best Deal)", "Access to all Premium Tools", "HD Render Unlocked"],
            capacities: ["~9 Matchmaker Sets", "~9 Nameplate Studio", "~9 Sketch to Real", "~4 Studio Mockups"],
            containerClass: 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10',
            buttonClass: 'bg-slate-900 text-white dark:bg-white dark:text-black hover:scale-105 shadow-lg shadow-slate-900/10 dark:shadow-white/10',
            badge: "LIMITED DEAL"
        },
        {
            id: 'silver', 
            name: 'Silver', 
            priceDisplay: '₹199', 
            price: 199,
            originalPriceDisplay: '₹399',
            originalPrice: 399,
            discountLabel: '50% OFF',
            swarnas: 400, 
            subDesc: "400 Swarnas / 30 Days",
            features: ["+ 400 Swarnas (Double Value)", "Access to all Premium Tools", "HD Render Unlocked"],
            capacities: ["~20 Matchmaker Sets", "~20 Nameplate Studio", "~20 Sketch to Real", "~10 Studio Mockups"],
            containerClass: 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10',
            buttonClass: 'bg-slate-900 text-white dark:bg-white dark:text-black hover:scale-105 shadow-lg shadow-slate-900/10 dark:shadow-white/10',
            badge: null
        },
        {
            id: 'gold', 
            name: 'Gold', 
            priceDisplay: '₹399', 
            price: 399,
            originalPriceDisplay: '₹799',
            originalPrice: 799,
            discountLabel: 'BEST VALUE',
            swarnas: 800, 
            subDesc: "800 Swarnas / 30 Days",
            features: ["+ 800 Swarnas", "Access to all Premium Tools", "HD Render Unlocked"],
            capacities: ["~40 Matchmaker Sets", "~40 Nameplate Studio", "~40 Sketch to Real", "~20 Studio Mockups"],
            badge: "POPULAR",
            containerClass: 'bg-gradient-to-b from-amber-50 to-white dark:from-amber-900/10 dark:to-white/5 border-amber-200 dark:border-amber-500/30 ring-1 ring-amber-500/20',
            buttonClass: 'bg-apple-gold text-white dark:text-black hover:scale-105 shadow-lg shadow-apple-gold/30',
        },
        {
            id: 'platinum', 
            name: 'Platinum', 
            priceDisplay: '₹999', 
            price: 999,
            originalPriceDisplay: '₹1999',
            originalPrice: 1999,
            discountLabel: 'PRO CHOICE',
            swarnas: 2000, 
            subDesc: "2000 Swarnas / 30 Days",
            features: ["+ 2000 Swarnas", "Access to all Premium Tools", "HD Render Unlocked", "Priority Support"],
            capacities: ["~100 Matchmaker Sets", "~100 Nameplate Studio", "~100 Sketch to Real", "~50 Studio Mockups"],
            containerClass: 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10',
            buttonClass: 'bg-slate-900 text-white dark:bg-white dark:text-black hover:scale-105 shadow-lg shadow-slate-900/10 dark:shadow-white/10',
            badge: null
        }
    ];

    const addOnPacks = [
        { id: 'pack_small', name: t('topUpSmall'), swarnas: 160, priceDisplay: '₹100', price: 100, desc: "Emergency Top-up", popular: false },
        { id: 'pack_medium', name: t('topUpMedium'), swarnas: 360, priceDisplay: '₹200', price: 200, desc: "Standard Top-up", popular: true },
        { id: 'pack_large', name: t('topUpLarge'), swarnas: 900, priceDisplay: '₹500', price: 500, desc: "Studio Top-up", popular: false }
    ];

    useEffect(() => {
        if (isOpen) {
            setViewMode(initialView);
            setError(null);
            setSuccessMessage(null);
            // Reset OTP state
            setPendingTransaction(null);
            setOtpStep('input');
            setMobileInput('');
            setOtp('');
        } else {
            // Clean up ReCAPTCHA on close
            if (recaptchaVerifier.current) {
                try {
                    recaptchaVerifier.current.clear();
                } catch(e) { console.error(e); }
                recaptchaVerifier.current = null;
            }
        }
    }, [isOpen, initialView]);

    if (!isOpen || !user) return null;

    // --- Actions ---

    const initiatePurchase = (id: string, type: 'subscription' | 'addon') => {
        // --- GOOGLE ANALYTICS TRACKING ---
        if (typeof window.gtag === 'function') {
            let price = 0;
            let name = id;
            
            if (type === 'subscription') {
                const item = subscriptions.find(s => s.id === id);
                if (item) { price = item.price; name = item.name; }
            } else {
                const item = addOnPacks.find(p => p.id === id);
                if (item) { price = item.price; name = item.name; }
            }

            window.gtag('event', 'begin_checkout', {
                currency: 'INR',
                value: price,
                items: [{
                    item_id: id,
                    item_name: name,
                    item_category: type,
                    price: price,
                    quantity: 1
                }]
            });
        }
        // ---------------------------------

        // Enforce Phone Verification:
        // If the user hasn't linked a phone number yet, they must do so before purchasing.
        const isPhoneVerified = auth.currentUser?.phoneNumber;

        if (isPhoneVerified) {
            executePurchase(id, type);
        } else {
            setPendingTransaction({ id, type });
            setViewMode('verify_phone');
            setOtpStep('input');
            setError(null);
        }
    };

    const setupRecaptcha = () => {
        // Clear existing verifier if present
        if (recaptchaVerifier.current) {
            try { recaptchaVerifier.current.clear(); } catch(e){}
            recaptchaVerifier.current = null;
        }

        const container = document.getElementById('recaptcha-container');
        if (container) {
            // Ensure container is empty
            container.innerHTML = '';
            
            try {
                recaptchaVerifier.current = new firebase.auth.RecaptchaVerifier('recaptcha-container', {
                    'size': 'invisible', 
                    'callback': () => {
                        console.log("Recaptcha solved");
                    },
                    'expired-callback': () => {
                        setError("Security check expired. Please try again.");
                        setIsSendingOtp(false);
                    }
                });
            } catch (e) {
                console.error("Recaptcha Init Error", e);
            }
        }
    };

    const handleSendOtp = async () => {
        if (mobileInput.length !== 10) {
            setError("Please enter a valid 10-digit mobile number.");
            return;
        }
        
        const fullPhoneNumber = `+91${mobileInput}`;
        
        setError(null);
        setIsSendingOtp(true);

        // Delay to allow DOM rendering of container if view just switched
        setTimeout(async () => {
            try {
                setupRecaptcha();
                const appVerifier = recaptchaVerifier.current;
                const currentUser = auth.currentUser;

                if (!currentUser || !appVerifier) {
                    setError("Security check initialization failed. Please refresh.");
                    setIsSendingOtp(false);
                    return;
                }

                try {
                    // Link phone to current user
                    const confirmation = await currentUser.linkWithPhoneNumber(fullPhoneNumber, appVerifier);
                    setConfirmationResult(confirmation);
                    setOtpStep('verify');
                    setIsSendingOtp(false);
                } catch (error: any) {
                    console.error("SMS Error:", error);
                    setIsSendingOtp(false);
                    
                    if (error.code === 'auth/invalid-phone-number') {
                        setError("Invalid phone number format.");
                    } else if (error.code === 'auth/too-many-requests') {
                        setError("Too many OTP requests. Please try again later.");
                    } else if (error.code === 'auth/credential-already-in-use') {
                        setError("This number is already registered with another user. Please use a different number.");
                    } else if (error.code === 'auth/unauthorized-domain') {
                        setError(`Domain unauthorized. Add '${window.location.hostname}' to Firebase Console.`);
                    } else {
                        setError("Failed to send OTP. " + (error.message || "Unknown error"));
                    }
                    
                    // Reset recaptcha on error
                    if (recaptchaVerifier.current) {
                        try { recaptchaVerifier.current.clear(); } catch(e){}
                        recaptchaVerifier.current = null;
                    }
                }
            } catch (e: any) {
                setIsSendingOtp(false);
                setError(e.message || "An unexpected error occurred.");
            }
        }, 500);
    };

    const handleVerifyOtp = async () => {
        if (!otp || !confirmationResult) return;
        setVerifying(true);
        setError(null);
        try {
            await confirmationResult.confirm(otp);
            
            // Sync phone number to Firestore user profile
            const currentUser = auth.currentUser;
            if (currentUser && currentUser.phoneNumber) {
                try {
                    await db.collection("users").doc(currentUser.uid).update({
                        phoneNumber: currentUser.phoneNumber
                    });
                } catch (dbError) {
                    console.error("Failed to sync phone number to DB:", dbError);
                    // Non-critical, proceed to purchase
                }
            }

            // OTP Success - Proceed to Payment
            setVerifying(false);
            if (pendingTransaction) {
                executePurchase(
                    pendingTransaction.id, 
                    pendingTransaction.type
                );
            }
        } catch (error: any) {
            setVerifying(false);
            console.error("OTP Verify Error:", error);
            if (error.code === 'auth/credential-already-in-use') {
                setError("This number is already registered with another user. Please use a different number.");
            } else if (error.code === 'auth/invalid-verification-code') {
                setError("Invalid OTP. Please check and try again.");
            } else {
                setError("Verification failed. " + (error.message || "Please try again."));
            }
        }
    };

    const executePurchase = async (id: string, type: 'subscription' | 'addon') => {
        // Return to overview
        setViewMode('overview'); 
        setProcessingId(id);
        setSuccessMessage(null);
        setError(null);
        try {
            await buyCredits(id, type);
            
            setSuccessMessage(t('paymentSuccess').replace('{amount}', 'Credits'));
            setTimeout(() => {
                setSuccessMessage(null);
                setProcessingId(null);
            }, 4000);
        } catch (error: any) {
            console.error("Purchase Flow Error:", error);
            if (error.message && (error.message.includes("cancelled") || error.message.includes("closed"))) {
                 setProcessingId(null);
            } else {
                 setError(error.message || t('paymentError'));
                 setProcessingId(null);
            }
        }
    };

    const handleVerifyMissingPayment = async () => {
        if (!pendingTx) return;
        setVerifying(true);
        // Uses stored id from context state
        const recovered = await checkPaymentStatus(); 
        setVerifying(false);
        if (recovered) {
            setSuccessMessage(`Payment verified! Credits added.`);
        } else {
            setError("We couldn't verify the payment. Any deducted amount will be auto-refunded in 5-7 days.");
            setTimeout(() => clearPendingOrder(), 6000); 
        }
    };

    // --- Helpers ---
    const getPlanName = (plan: string) => {
        switch(plan) {
            case 'basic': return 'Basic';
            case 'bronze': return 'Bronze';
            case 'silver': return 'Silver';
            case 'gold': return 'Gold';
            case 'platinum': return 'Platinum';
            default: return plan;
        }
    };

    const formatDate = (isoString?: string) => {
        if (!isoString) return "-";
        try {
            return new Date(isoString).toLocaleDateString('en-IN', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                timeZone: 'Asia/Kolkata'
            });
        } catch (e) {
            return isoString;
        }
    };

    // --- Calculations ---
    const bonusCredits = 150;
    const subCredits = transactions.filter(t => t.description.includes('Plan') || t.description.includes('Subscription') || t.description.includes('Bronze') || t.description.includes('Silver')).reduce((acc, t) => acc + t.swarnas, 0);
    const purchasedCredits = transactions.filter(t => t.description.includes('Pack') || t.description.includes('Top-up')).reduce((acc, t) => acc + t.swarnas, 0);
    const remainingCredits = user.swarnas;

    const filteredTransactions = transactions.filter(t => {
        if (transactionFilter === 'gained') return t.swarnas > 0;
        if (transactionFilter === 'consumed') return t.swarnas < 0;
        return true;
    });

    const hasActiveSubscription = user.plan !== 'basic';

    const getTitle = () => {
        if (viewMode === 'verify_phone') return "Verify Mobile";
        if (viewMode === 'credits') return t('titleCreditsDetails');
        if (viewMode === 'subscription') return "Plan Details";
        return t('pricingTitle');
    };

    return (
        <div className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/40 dark:bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
            <div className="w-full sm:max-w-6xl bg-[#F5F5F7] dark:bg-[#1d1d1f] rounded-t-3xl sm:rounded-apple shadow-2xl overflow-hidden flex flex-col h-[92vh] sm:h-auto sm:max-h-[90vh] border-t sm:border border-white/50 dark:border-white/10 relative transition-all">
                
                {/* --- SAFETY NET: PENDING PAYMENT ALERT --- */}
                {pendingTx && viewMode !== 'verify_phone' && (
                    <div className="shrink-0 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-6 py-3 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm font-medium border-b border-amber-200 dark:border-amber-800/50 z-40">
                        <div className="flex items-center gap-2">
                            <Icon name="refresh" className="w-5 h-5 animate-spin-slow" />
                            <span>Incomplete transaction detected.</span>
                        </div>
                        <button 
                            onClick={handleVerifyMissingPayment} 
                            disabled={verifying}
                            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold transition-colors shadow-sm whitespace-nowrap"
                        >
                            {verifying ? "Checking Bank..." : "Verify Status"}
                        </button>
                    </div>
                )}

                {/* --- WELCOME BONUS BANNER (Psychological Reciprocity) --- */}
                {viewMode === 'overview' && user.plan === 'basic' && (
                    <div className="shrink-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white/10 dark:via-white/5 dark:to-white/10 text-white px-6 py-2.5 flex items-center justify-center gap-2 text-sm font-medium border-b border-white/10 z-30">
                        <span className="w-6 h-6 rounded-full bg-apple-gold text-black flex items-center justify-center"><Icon name="diamond" className="w-3 h-3" /></span>
                        <span>Welcome Gift: You unlocked <strong>150 Joining Bonus Swarnas!</strong></span>
                    </div>
                )}

                {/* --- HEADER --- */}
                <div className="p-4 md:p-8 border-b border-black/5 dark:border-white/5 bg-white/80 dark:bg-[#1d1d1f]/90 relative shrink-0 backdrop-blur-xl z-20">
                    <div className="flex justify-between items-start md:items-center mb-4 md:mb-6">
                         <h2 className="text-xl md:text-3xl font-display font-bold text-slate-900 dark:text-white flex items-center gap-2 md:gap-3">
                            <span className="text-apple-gold">|</span> {getTitle()}
                        </h2>
                        <button onClick={onClose} className="p-2 bg-black/5 dark:bg-white/10 rounded-full text-slate-500 dark:text-white hover:bg-black/10 dark:hover:bg-white/20 transition-colors">
                            <Icon name="close" className="w-5 h-5" />
                        </button>
                    </div>

                     {viewMode !== 'verify_phone' && (
                         <div className="flex flex-wrap gap-2 mb-4 md:mb-6">
                            {viewMode === 'overview' ? (
                                <>
                                    {user.plan !== 'basic' && (
                                        <button 
                                            onClick={() => setViewMode('subscription')} 
                                            className="flex-1 md:flex-none px-4 py-2.5 bg-slate-900 dark:bg-white/10 text-white border border-transparent rounded-xl text-xs font-bold transition-colors hover:scale-105 shadow-md"
                                        >
                                            {t('btnManageSub')}
                                        </button>
                                    )}
                                    <button onClick={() => setViewMode('credits')} className="flex-1 md:flex-none px-4 py-2.5 bg-white dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/10 transition-colors">
                                        {t('btnViewDetails')}
                                    </button>
                                </>
                            ) : (
                                <button onClick={() => setViewMode('overview')} className="px-4 py-2.5 bg-white dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/10 transition-colors flex items-center gap-2">
                                    <Icon name="close" className="w-3 h-3" /> Back to Plans
                                </button>
                            )}
                        </div>
                     )}

                    {/* Mini Dashboard Summary (Always Visible) */}
                    {viewMode !== 'verify_phone' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-8 p-3 md:p-4 bg-white/50 dark:bg-white/5 rounded-2xl border border-black/5 dark:border-white/5">
                            <div className="p-1">
                                <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mb-1">{t('currentPlan')}</p>
                                <p className="text-base md:text-xl font-bold text-slate-900 dark:text-white">{getPlanName(user.plan)}</p>
                            </div>
                            <div className="p-1">
                                <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mb-1">
                                    {t('planExpiresOn')}
                                </p>
                                <p className="text-base md:text-xl font-bold text-slate-900 dark:text-white">{formatDate(user.subscriptionEndDate)}</p>
                            </div>
                            <div className="p-1">
                                <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mb-1">{t('lblRemainingCredits')}</p>
                                <div className="flex items-center gap-2">
                                    <Icon name="diamond" className="w-4 h-4 md:w-5 md:h-5 text-apple-gold" />
                                    <p className="text-base md:text-xl font-bold text-slate-900 dark:text-white">{remainingCredits}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* --- NOTIFICATIONS BAR (FIXED ON TOP) --- */}
                {/* Moved outside the scroll container to ensure high visibility */}
                <div className="shrink-0 z-30 relative">
                    {successMessage && (
                        <div className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 px-6 py-3 flex items-center justify-between gap-3 text-sm font-bold border-b border-green-200 dark:border-green-800/50 animate-in slide-in-from-top-2 duration-300">
                            <div className="flex items-center gap-2">
                                <Icon name="check" className="w-5 h-5" />
                                <span>{successMessage}</span>
                            </div>
                            <button onClick={() => setSuccessMessage(null)}><Icon name="close" className="w-4 h-4" /></button>
                        </div>
                    )}
                    {error && (
                        <div className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 px-6 py-3 flex items-center justify-between gap-3 text-sm font-bold border-b border-red-200 dark:border-red-800/50 animate-in slide-in-from-top-2 duration-300">
                            <div className="flex items-center gap-2">
                                <Icon name="close" className="w-5 h-5 shrink-0" />
                                <span>{error}</span>
                            </div>
                            <button onClick={() => setError(null)}><Icon name="close" className="w-4 h-4" /></button>
                        </div>
                    )}
                </div>

                <div className="flex-grow overflow-y-auto custom-scrollbar bg-slate-50/50 dark:bg-black/20 pb-8">
                    
                    {viewMode === 'verify_phone' && (
                        <div className="flex flex-col items-center justify-center p-8 h-full animate-in fade-in slide-in-from-right-4 duration-300 relative">
                            <div className="w-full max-w-md bg-white dark:bg-white/5 p-8 rounded-3xl border border-black/5 dark:border-white/5 shadow-xl text-center relative z-10">
                                <div className="w-20 h-20 bg-apple-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Icon name="diamond" className="w-10 h-10 text-apple-gold" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Secure Verification</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
                                    To protect your transactions, we need to verify your mobile number before your first purchase.
                                </p>

                                {otpStep === 'input' ? (
                                    <div className="space-y-4">
                                        <div className="flex items-center bg-slate-50 dark:bg-black/30 border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-apple-gold transition-shadow">
                                            <div className="bg-slate-100 dark:bg-white/5 px-4 py-4 text-slate-500 dark:text-slate-400 font-bold text-xl tracking-wider border-r border-slate-200 dark:border-white/10 select-none">
                                                +91
                                            </div>
                                            <input
                                                type="tel"
                                                value={mobileInput}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                    setMobileInput(val);
                                                }}
                                                className="w-full px-4 py-4 bg-transparent text-xl font-bold tracking-widest outline-none text-slate-900 dark:text-white placeholder-slate-300 dark:placeholder-slate-600"
                                                placeholder="98765 43210"
                                            />
                                        </div>
                                        
                                        <div className="flex gap-3">
                                            <button 
                                                onClick={() => setViewMode('overview')}
                                                className="flex-1 py-3.5 bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-white/20 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSendOtp}
                                                disabled={isSendingOtp || mobileInput.length !== 10}
                                                className="flex-1 py-3.5 bg-slate-900 dark:bg-white text-white dark:text-black font-bold rounded-xl hover:scale-[1.02] active:scale-95 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isSendingOtp ? <Spinner /> : "Send OTP"}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4 animate-in fade-in zoom-in duration-300">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={otp}
                                                maxLength={6}
                                                onChange={(e) => setOtp(e.target.value)}
                                                className="w-full text-center text-2xl font-bold tracking-[0.5em] py-4 bg-slate-50 dark:bg-black/30 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-apple-gold outline-none"
                                                placeholder="000000"
                                            />
                                        </div>
                                        <div className="flex gap-3">
                                            <button 
                                                onClick={() => { setOtpStep('input'); setOtp(''); }}
                                                className="flex-1 py-3.5 bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-white/20 transition-colors"
                                            >
                                                Back
                                            </button>
                                            <button
                                                onClick={handleVerifyOtp}
                                                disabled={verifying || otp.length !== 6}
                                                className="flex-1 py-3.5 bg-green-500 text-white font-bold rounded-xl hover:bg-green-600 active:scale-95 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                                            >
                                                {verifying ? <Spinner /> : "Verify & Pay"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* ReCAPTCHA container */}
                            <div id="recaptcha-container"></div>
                        </div>
                    )}

                    {viewMode === 'overview' && (
                        <div className="p-4 md:p-8 relative">
                            <div>
                                <div className="flex flex-col items-center mb-6 md:mb-10">
                                    <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                                        <Icon name="sparkles" className="w-5 h-5 md:w-6 md:h-6 text-apple-gold" /> {t('tabSubscriptions')}
                                    </h3>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                                    {subscriptions.map((plan) => (
                                        <div key={plan.id} className={`relative ${plan.containerClass} border rounded-3xl p-5 md:p-6 flex flex-col hover:scale-[1.02] transition-transform duration-300 shadow-sm`}>
                                            {plan.badge && (
                                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-apple-gold text-white dark:text-black text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-lg">
                                                    {plan.badge}
                                                </div>
                                            )}
                                            <div className="mb-4 md:mb-6">
                                                <h4 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white mb-1">{plan.name}</h4>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{plan.subDesc}</p>
                                            </div>
                                            
                                            {/* --- PRICING DISPLAY (PSYCHOLOGY: ANCHORING) --- */}
                                            <div className="flex flex-col mb-6 md:mb-8">
                                                {plan.originalPrice > 0 && (
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs text-slate-400 line-through decoration-slate-400/50 font-medium">
                                                            {plan.originalPriceDisplay}
                                                        </span>
                                                        <span className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[9px] font-black px-1.5 py-0.5 rounded border border-red-100 dark:border-red-900/50">
                                                            {plan.discountLabel}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">{plan.priceDisplay}</span>
                                                    <span className="text-[10px] md:text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">/ 30 Days</span>
                                                </div>
                                            </div>
                                            
                                            <div className="flex-grow space-y-3 md:space-y-4 mb-6 md:mb-8">
                                                <div className="space-y-2 md:space-y-3">
                                                    {plan.features.map((f, i) => (
                                                        <div key={i} className="flex items-start gap-3 text-xs text-slate-600 dark:text-slate-300 font-bold">
                                                            <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-apple-gold">
                                                                <Icon name="check" className="w-3 h-3" />
                                                            </div>
                                                            {f}
                                                        </div>
                                                    ))}
                                                </div>
                                                {plan.capacities.length > 0 && (
                                                    <div className="pt-4 border-t border-black/5 dark:border-white/5 space-y-2">
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">{t('capacityLabel')}</p>
                                                        {plan.capacities.map((c, i) => (
                                                            <div key={i} className="flex flex-wrap items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                                                                <span>{c}</span>
                                                                {i < plan.capacities.length - 1 && (
                                                                    <span className="text-[9px] font-black text-slate-300 uppercase px-1">{t('or')}</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <button
                                                onClick={() => plan.id !== 'basic' && initiatePurchase(plan.id, 'subscription')}
                                                disabled={plan.id === 'basic' || !!processingId || (user.plan === plan.id)}
                                                className={`w-full py-3 md:py-4 rounded-full font-bold text-sm transition-all ${plan.buttonClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                                            >
                                                {processingId === plan.id ? <Spinner /> : (user.plan === plan.id ? "Extended" : (user.plan === 'basic' ? t('subscribe') : "Activate"))}
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {hasActiveSubscription && (
                                    <div className="mb-10 mt-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                        <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                                            <Icon name="plus" className="w-5 h-5 text-apple-gold" />
                                            {t('tabTopUps')}
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {addOnPacks.map(pack => (
                                                <div key={pack.id} className="relative bg-white dark:bg-white/5 border border-black/5 dark:border-white/5 rounded-2xl p-5 flex flex-col items-center text-center shadow-sm hover:scale-[1.02] transition-transform">
                                                    {pack.popular && (
                                                        <div className="absolute top-0 right-0 bg-apple-gold text-white text-[10px] font-bold px-2 py-1 rounded-bl-xl rounded-tr-xl">
                                                            POPULAR
                                                        </div>
                                                    )}
                                                    <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-white/10 flex items-center justify-center mb-3 text-apple-gold">
                                                        <Icon name="diamond" className="w-6 h-6" />
                                                    </div>
                                                    <h5 className="font-bold text-slate-900 dark:text-white">{pack.name}</h5>
                                                    <p className="text-2xl font-bold text-slate-900 dark:text-white my-2">{pack.swarnas} <span className="text-xs font-normal text-slate-500">Swarnas</span></p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{pack.priceDisplay}</p>
                                                    <button
                                                        onClick={() => initiatePurchase(pack.id, 'addon')}
                                                        disabled={!!processingId}
                                                        className="w-full py-2 rounded-xl font-bold text-xs flex items-center justify-center transition-all bg-slate-900 dark:bg-white text-white dark:text-black hover:opacity-90"
                                                    >
                                                        {processingId === pack.id ? <Spinner /> : t('buyPack')}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {viewMode === 'subscription' && (
                        <div className="p-4 md:p-8 max-w-4xl mx-auto animate-in fade-in slide-in-from-right-2 duration-300">
                            <div className="bg-white dark:bg-white/5 rounded-3xl border border-black/5 dark:border-white/5 overflow-hidden shadow-lg">
                                <div className="p-8 md:p-12 text-center border-b border-black/5 dark:border-white/5 bg-slate-50 dark:bg-black/20">
                                    <div className="w-24 h-24 rounded-full bg-apple-gold/10 flex items-center justify-center mx-auto mb-6 text-apple-gold shadow-lg shadow-apple-gold/20">
                                        <Icon name="diamond" className="w-12 h-12" />
                                    </div>
                                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">{getPlanName(user.plan)}</h3>
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-green-100 text-green-700">
                                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                        Plan Active
                                    </div>
                                </div>
                                <div className="p-8 md:p-12 space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-2">
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Plan Cost</p>
                                            <p className="text-xl font-bold text-slate-900 dark:text-white">
                                                {subscriptions.find(s => s.id === user.plan)?.priceDisplay || '-'} <span className="text-sm font-normal text-slate-500">/ 30 Days</span>
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                                                {t('planExpiresOn')}
                                            </p>
                                            <p className="text-xl font-bold text-slate-900 dark:text-white">{formatDate(user.subscriptionEndDate)}</p>
                                        </div>
                                    </div>

                                    <div className="bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-black/5 dark:border-white/5">
                                        <h4 className="font-bold text-slate-900 dark:text-white mb-4">Plan Benefits</h4>
                                        <ul className="space-y-3">
                                            {subscriptions.find(s => s.id === user.plan)?.features.map((f, i) => (
                                                <li key={i} className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
                                                    <Icon name="check" className="w-4 h-4 text-apple-gold" />
                                                    {f}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="pt-4 border-t border-black/5 dark:border-white/5 flex flex-col items-center gap-4">
                                        <p className="text-xs text-slate-400 text-center max-w-sm">
                                            This is a prepaid validity plan. Your benefits will automatically expire on {formatDate(user.subscriptionEndDate)} unless you recharge.
                                        </p>
                                        <button 
                                            onClick={() => setViewMode('overview')}
                                            className="w-full md:w-auto px-8 py-3 bg-slate-900 dark:bg-white text-white dark:text-black rounded-full font-bold transition-all hover:scale-105 shadow-lg"
                                        >
                                            Extend Plan Validity
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {viewMode === 'credits' && (
                        <div className="p-4 md:p-8">
                            <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-6 md:mb-8">{t('titleCreditsDetails')}</h3>
                            
                            <div className="bg-white dark:bg-white/5 p-4 md:p-6 rounded-2xl border border-black/5 dark:border-white/5 flex flex-wrap items-center justify-between gap-4 mb-8 md:mb-10 shadow-sm">
                                <div className="text-center w-full sm:w-auto">
                                    <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider mb-1">{t('colRemaining')}</p>
                                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{remainingCredits}</p>
                                </div>
                                <div className="hidden sm:block text-2xl font-bold text-slate-300 dark:text-slate-700">=</div>
                                <div className="flex flex-wrap justify-center sm:justify-between w-full sm:w-auto gap-4 sm:gap-8">
                                    <div className="text-center group relative cursor-help">
                                        <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                                            {t('colBonus')} <Icon name="help" className="w-3 h-3 opacity-50" />
                                        </p>
                                        <p className="text-lg md:text-2xl font-bold text-slate-400 dark:text-slate-500">{bonusCredits}</p>
                                    </div>
                                    <div className="text-xl font-bold text-slate-300 dark:text-slate-700 self-center">+</div>
                                    <div className="text-center group relative cursor-help">
                                        <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                                            {t('colSub')} <Icon name="help" className="w-3 h-3 opacity-50" />
                                        </p>
                                        <p className="text-lg md:text-2xl font-bold text-slate-400 dark:text-slate-500">{subCredits}</p>
                                    </div>
                                    <div className="text-xl font-bold text-slate-300 dark:text-slate-700 self-center">+</div>
                                    <div className="text-center group relative cursor-help">
                                        <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
                                            {t('colPurchased')} <Icon name="help" className="w-3 h-3 opacity-50" />
                                        </p>
                                        <p className="text-lg md:text-2xl font-bold text-slate-400 dark:text-slate-500">{purchasedCredits}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-white/5 rounded-3xl border border-black/5 dark:border-white/5 overflow-hidden shadow-sm">
                                <div className="p-2 bg-slate-50 dark:bg-white/5 flex justify-center border-b border-black/5 dark:border-white/5 overflow-x-auto">
                                    <div className="flex p-1 bg-white dark:bg-black/20 rounded-full border border-black/5 dark:border-white/5 whitespace-nowrap">
                                        <button onClick={() => setTransactionFilter('all')} className={`px-4 md:px-6 py-2 rounded-full text-xs font-bold transition-all ${transactionFilter === 'all' ? 'bg-slate-900 text-white dark:bg-white dark:text-black shadow-md' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}>{t('tabAll')}</button>
                                        <button onClick={() => setTransactionFilter('gained')} className={`px-4 md:px-6 py-2 rounded-full text-xs font-bold transition-all ${transactionFilter === 'gained' ? 'bg-slate-900 text-white dark:bg-white dark:text-black shadow-md' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}>{t('tabGained')}</button>
                                        <button onClick={() => setTransactionFilter('consumed')} className={`px-4 md:px-6 py-2 rounded-full text-xs font-bold transition-all ${transactionFilter === 'consumed' ? 'bg-slate-900 text-white dark:bg-white dark:text-black shadow-md' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}>{t('tabConsumed')}</button>
                                    </div>
                                </div>
                                
                                <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                                    {filteredTransactions.length === 0 ? (
                                        <div className="p-12 text-center text-slate-400">{t('noTransactions')}</div>
                                    ) : (
                                        <>
                                            <table className="w-full text-left hidden md:table">
                                                <tbody className="divide-y divide-black/5 dark:divide-white/5">
                                                    {filteredTransactions.map((t) => (
                                                        <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                                                            <td className="p-6 text-sm text-slate-400 font-mono">{t.date}</td>
                                                            <td className="p-6 text-sm font-medium text-slate-700 dark:text-slate-200">{t.description}</td>
                                                            <td className={`p-6 text-sm font-bold text-right ${t.swarnas > 0 ? 'text-green-500' : 'text-red-500 dark:text-red-400'}`}>
                                                                {t.swarnas > 0 ? '+' : ''}{t.swarnas}
                                                            </td>
                                                            <td className="p-6 text-sm font-bold text-right text-slate-700 dark:text-slate-300">
                                                                {t.balance !== undefined ? t.balance : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            <div className="md:hidden p-4 space-y-3">
                                                {filteredTransactions.map((t) => (
                                                    <div key={t.id} className="bg-slate-50 dark:bg-white/5 p-4 rounded-2xl border border-black/5 dark:border-white/5">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div className="text-sm font-bold text-slate-900 dark:text-white">{t.description}</div>
                                                            <div className={`text-sm font-bold ${t.swarnas > 0 ? 'text-green-500' : 'text-red-500 dark:text-red-400'}`}>
                                                                {t.swarnas > 0 ? '+' : ''}{t.swarnas}
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-between items-center mt-2">
                                                            <div className="text-[10px] font-mono text-slate-400">{t.date}</div>
                                                            {t.balance !== undefined && (
                                                                <div className="px-2 py-1 text-[10px] font-bold bg-slate-200 dark:bg-white/10 rounded-lg text-slate-600 dark:text-slate-300">
                                                                    Bal: {t.balance}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PricingModal;